import { useCallback, useState } from 'react'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { IncludedResource, SubscriptionPlacement } from '@/api-client/data-contracts'

export const PLACEMENTS_PER_PAGE = 10

const PLACEMENTS_INCLUDE = [
  'order',
  'order.address',
  'order.order_items',
  'order.order_items.shipping_statuses',
  'schedule',
  'schedule.cultivars',
]

// 生成された型にはrelationshipsが含まれていないので補う
export type OrderedPlacement = SubscriptionPlacement & {
  relationships: {
    order: { data: { id: string; type: string } | null }
    schedule: { data: { id: string; type: string } | null }
  }
}

export type SubscriptionCategory = 'lossless_bouquet' | 'rose' | 'green'

export function fetchSubscriptionOrderedPlacements(category: SubscriptionCategory, page: number, perPage: number = PLACEMENTS_PER_PAGE) {
  return createClient(Subscriptions).getSubscriptionsPlacementsOrdered({
    category,
    include: PLACEMENTS_INCLUDE,
    page: page.toString(),
    per_page: perPage.toString(),
  })
}

type Params = {
  category: SubscriptionCategory
  initialPlacements: OrderedPlacement[]
  initialIncluded?: IncludedResource[]
  perPage?: number
}

// 定期便の注文済みの予定をページ単位で読み足していく。
// loadMoreは多重実行と末尾到達をガードしているので、呼び出し側は素直に呼べばよい
export function useSubscriptionOrderedPlacements({
  category,
  initialPlacements,
  initialIncluded,
  perPage = PLACEMENTS_PER_PAGE,
}: Params) {
  const [placements, setPlacements] = useState<OrderedPlacement[]>(initialPlacements)
  const [included, setIncluded] = useState<IncludedResource[]>(initialIncluded ?? [])
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(initialPlacements.length >= perPage)

  const loadMore = useCallback(async () => {
    if (loading || !hasNextPage) return

    setLoading(true)

    try {
      const nextPage = page + 1
      const { data } = await fetchSubscriptionOrderedPlacements(category, nextPage, perPage)

      setPlacements((prev) => [...prev, ...(data.data as OrderedPlacement[])])
      setIncluded((prev) => [...prev, ...(data.included ?? [])])
      setPage(nextPage)
      setHasNextPage(data.data.length >= perPage)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [category, page, perPage, loading, hasNextPage])

  return { placements, included, hasNextPage, loading, loadMore }
}
